import { useMemo, useState } from 'react'
import { Box, Checkbox, MenuItem, Select, Stack, Typography } from '@mui/material'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import { Virtuoso } from 'react-virtuoso'
import { primary } from '../../../theme/palette'
import type { Asset } from '@cmms/types'
import { BulkActionBar } from '../../../components/BulkActionBar'
import type { useBulkSelection } from '../../../hooks/useBulkSelection'

const STATUS_DOT: Record<string, string> = {
  active: '#059669',
  inactive: '#94A3B8',
  under_maintenance: '#D97706',
  decommissioned: '#DC2626',
}

type SortKey = 'name-asc' | 'name-desc' | 'status' | 'recent'

interface Props {
  assets: Asset[]
  selectedId: string | null
  onSelect: (id: string) => void
  /// Optional bulk-selection harness. When omitted the row checkboxes
  /// and bottom action bar are hidden — the page hands this in only
  /// for users with the `assets:delete` permission.
  bulk?: ReturnType<typeof useBulkSelection>
  /// Triggered when the user clicks "Delete selected" in the bar.
  onBulkDelete?: () => void
  bulkDeleting?: boolean
}

export function AssetList({ assets, selectedId, onSelect, bulk, onBulkDelete, bulkDeleting }: Props) {
  const [sort, setSort] = useState<SortKey>('name-asc')

  const sorted = useMemo(() => {
    const list = [...assets]
    switch (sort) {
      case 'name-asc':  return list.sort((a, b) => a.name.localeCompare(b.name))
      case 'name-desc': return list.sort((a, b) => b.name.localeCompare(a.name))
      case 'status':    return list.sort((a, b) => a.status.localeCompare(b.status))
      case 'recent':    return list.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    }
  }, [assets, sort])

  return (
    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0, width: { xs: '100%', md: 360 }, flexShrink: 0,
      borderRight: { xs: 'none', md: '1px solid' }, borderBottom: { xs: 'none', md: 'none' }, borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Stack direction="row" alignItems="center" spacing={1}
        sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        {bulk && sorted.length > 0 && (
          <Checkbox size="small"
            checked={bulk.allSelected(sorted.map((a) => a.id))}
            indeterminate={bulk.someSelected(sorted.map((a) => a.id))}
            onChange={(e) => bulk.replaceWith(e.target.checked ? sorted.map((a) => a.id) : [])}
            sx={{ p: 0.5 }} />
        )}
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 500 }}>
          {bulk && bulk.count > 0 ? `${bulk.count} selected · Sort by` : 'Sort by'}
        </Typography>
        <Select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}
          size="small" variant="standard" disableUnderline
          sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', '& .MuiSelect-select': { py: 0, pr: '20px !important' }, '& .MuiSelect-icon': { color: 'primary.main', fontSize: 18 } }}>
          <MenuItem value="name-asc" sx={{ fontSize: 13 }}>Name · A to Z</MenuItem>
          <MenuItem value="name-desc" sx={{ fontSize: 13 }}>Name · Z to A</MenuItem>
          <MenuItem value="status" sx={{ fontSize: 13 }}>Status</MenuItem>
          <MenuItem value="recent" sx={{ fontSize: 13 }}>Recently updated</MenuItem>
        </Select>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{sorted.length}</Typography>
      </Stack>

      {sorted.length === 0 ? (
        <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No assets match your filters.</Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {/* Virtualized via react-virtuoso (same pattern as the Work
              Orders Kanban/Table views) so only the rows currently in
              view actually mount — with thousands of assets, rendering
              every row as a real DOM node made every click reconcile
              the whole list instead of just the (de)selected rows. */}
          <Virtuoso
            style={{ height: '100%' }}
            data={sorted}
            increaseViewportBy={200}
            itemContent={(_idx, asset) => {
              const sel = asset.id === selectedId
              const dot = STATUS_DOT[asset.status] ?? '#94A3B8'
              const catColor = asset.category_color || '#64748B'
              return (
                <Stack direction="row" alignItems="center" spacing={1.5} onClick={() => onSelect(asset.id)}
                  sx={{
                    position: 'relative', px: 1.5, py: 1.25, cursor: 'pointer',
                    transition: 'background-color 120ms ease',
                    bgcolor: sel ? primary.selected : 'transparent',
                    '&:hover': { bgcolor: sel ? primary.selectedHover : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.035)' },
                    '&::before': sel ? { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: 'primary.main' } : undefined,
                  }}>
                  {bulk && (
                    <Checkbox size="small" checked={bulk.isSelected(asset.id)}
                      onClick={(e) => { e.stopPropagation(); bulk.toggle(asset.id) }}
                      onChange={() => {}}
                      sx={{ p: 0.5 }} />
                  )}
                  <Box sx={{ width: 38, height: 38, borderRadius: 1.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: `${catColor}18`, color: catColor, flexShrink: 0, position: 'relative' }}>
                    <HubOutlinedIcon sx={{ fontSize: 18 }} />
                    <Box sx={{ position: 'absolute', right: -2, bottom: -2, width: 10, height: 10, borderRadius: 99, bgcolor: dot, border: '2px solid', borderColor: 'background.paper' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: sel ? 600 : 500, letterSpacing: '-0.005em' }} noWrap>{asset.name}</Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap rowGap={0.3} sx={{ mt: 0.2 }}>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{asset.asset_code}</Typography>
                      {asset.category_name && (
                        <>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>·</Typography>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{asset.category_name}</Typography>
                        </>
                      )}
                      {asset.criticality !== 'normal' && (() => {
                        const critical = asset.criticality === 'critical'
                        return (
                          <Box component="span" sx={{
                            fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            px: 0.55, py: 0.05, borderRadius: 0.5,
                            bgcolor: critical ? '#DC262614' : '#D9770614',
                            color: critical ? '#DC2626' : '#D97706',
                          }}>
                            {critical ? 'Critical' : 'Important'}
                          </Box>
                        )
                      })()}
                      {asset.warranty_expiry && (() => {
                        const inWarranty = new Date(asset.warranty_expiry).getTime() >= Date.now()
                        return (
                          <Box component="span" sx={{
                            fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            px: 0.55, py: 0.05, borderRadius: 0.5,
                            bgcolor: inWarranty ? '#05966914' : '#DC262614',
                            color: inWarranty ? '#059669' : '#DC2626',
                          }}>
                            {inWarranty ? 'In warranty' : 'Out of warranty'}
                          </Box>
                        )
                      })()}
                    </Stack>
                  </Box>
                </Stack>
              )
            }}
          />
        </Box>
      )}
      {bulk && onBulkDelete && (
        <BulkActionBar count={bulk.count} label="asset"
          deleting={bulkDeleting}
          onClear={bulk.clear} onDelete={onBulkDelete} />
      )}
    </Box>
  )
}
